import React, { useState, useEffect } from 'react';
import {
    Modal, Form, Select, Switch, InputNumber, Input, DatePicker,
    Button, Card, Typography, Divider, Space, Tag, Popconfirm, message, Alert, Row, Col
} from 'antd';
import {
    UserOutlined, EnvironmentOutlined, CalendarOutlined,
    PlusOutlined, DeleteOutlined, CheckCircleOutlined,
    SendOutlined, ExclamationCircleOutlined, SafetyCertificateOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import axios from 'axios';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { AdventureDto, InvitationDto, PlaySpaceDto } from '../../commons/data/types';
import { useAuthContext } from '../auth/LauncherAuthContext';

const { Title, Text, Paragraph } = Typography;
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

interface InvitationManagementModalProps {
    open: boolean;
    adventure: AdventureDto | null;
    invitation: InvitationDto | null;
    onClose: () => void;
    onSuccess: () => void;
}

export const InvitationManagementModal: React.FC<InvitationManagementModalProps> = ({
    open,
    adventure,
    invitation,
    onClose,
    onSuccess
}) => {
    const { t } = useTranslation();
    const { user } = useAuthContext();
    const organizer = user?.organizer;
    const [form] = Form.useForm();

    const [currentInvitation, setCurrentInvitation] = useState<InvitationDto | null>(invitation);
    const [saving, setSaving] = useState(false);
    const [addingSession, setAddingSession] = useState(false);
    const [newSessionTime, setNewSessionTime] = useState<Dayjs | null>(null);

    // Fetch PlaySpaces of current organizer
    const { data: playSpaces = [], isLoading: loadingPlaySpaces } = useQuery<PlaySpaceDto[]>(
        'organizer-playspaces',
        () => axios.get('/api/organizer/playspaces').then(res => res.data),
        { enabled: open && !!organizer }
    );

    useEffect(() => {
        if (open) {
            setCurrentInvitation(invitation);
            if (invitation) {
                form.setFieldsValue({
                    gmId: invitation.gmId,
                    playSpaceId: invitation.playSpaceId,
                    approvalRequired: invitation.approvalRequired,
                    preexistingPlayers: invitation.preexistingPlayers,
                    minTotalPlayers: invitation.minTotalPlayers,
                    maxTotalPlayers: invitation.maxTotalPlayers,
                    invitationDeadline: invitation.invitationDeadline ? dayjs(invitation.invitationDeadline) : null,
                    cancelationPolicy: invitation.cancelationPolicy,
                    costPerSession: invitation.costPerSession
                });
            } else {
                form.resetFields();
                form.setFieldsValue({
                    approvalRequired: true,
                    preexistingPlayers: 0,
                    minTotalPlayers: 1,
                    maxTotalPlayers: 5,
                    costPerSession: 0
                });
            }
        }
    }, [open, invitation, form]);

    if (!adventure || !organizer) return null;

    const gameMasters = organizer.gameMasters || [];

    const minSessionDate = currentInvitation?.invitationDeadline
        ? dayjs(currentInvitation.invitationDeadline).add(1, 'day')
        : null;

    const getErrorMessage = (err: any) => {
        const code = err.response?.data;
        if (typeof code === 'string' && code) {
            const translated = t(`errors.${code}`);
            if (translated && translated !== `errors.${code}`) {
                return translated;
            }
            return code;
        }
        return t('common.failure');
    };

    const handleSaveInvitation = async (values: any) => {
        try {
            setSaving(true);
            const deadlineMs = values.invitationDeadline ? values.invitationDeadline.valueOf() : Date.now();

            const payload: Partial<InvitationDto> = {
                gmId: values.gmId,
                playSpaceId: values.playSpaceId,
                approvalRequired: !!values.approvalRequired,
                preexistingPlayers: values.preexistingPlayers || 0,
                minTotalPlayers: values.minTotalPlayers || 1,
                maxTotalPlayers: values.maxTotalPlayers || 5,
                invitationDeadline: deadlineMs,
                cancelationPolicy: (values.cancelationPolicy || '').trim(),
                costPerSession: values.costPerSession || 0
            };

            let res;
            if (currentInvitation?.id) {
                res = await axios.put(`/api/organizer/invitations/${currentInvitation.id}`, payload);
                message.success(t('organizer.invitations.success.updated'));
            } else {
                res = await axios.post(`/api/organizer/adventures/${adventure.id}/invitations`, payload);
                message.success(t('organizer.invitations.success.details'));
            }

            setCurrentInvitation(res.data);
            onSuccess();
        } catch (err: any) {
            message.error(getErrorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteInvitation = async () => {
        if (!currentInvitation?.id) return;
        try {
            await axios.delete(`/api/organizer/invitations/${currentInvitation.id}`);
            message.success(t('organizer.invitations.success.deleted'));
            onSuccess();
            onClose();
        } catch (err: any) {
            message.error(getErrorMessage(err));
        }
    };

    const handlePublishInvitation = async () => {
        if (!currentInvitation?.id) return;
        try {
            const res = await axios.post(`/api/organizer/invitations/${currentInvitation.id}/publish`);
            message.success(t('organizer.invitations.success.published'));
            setCurrentInvitation(res.data);
            onSuccess();
        } catch (err: any) {
            message.error(getErrorMessage(err));
        }
    };

    const handleAddSession = async () => {
        if (!currentInvitation?.id || !newSessionTime) return;

        const newTimeMs = newSessionTime.valueOf();
        const existingSessions = currentInvitation.sessions || [];

        // 24-hour separation check
        const isTooClose = existingSessions.some(sess => Math.abs(sess.time - newTimeMs) < TWENTY_FOUR_HOURS_MS);
        if (isTooClose) {
            message.error(t('organizer.invitations.error.sessionTooClose'));
            return;
        }

        try {
            setAddingSession(true);
            const res = await axios.post(`/api/organizer/invitations/${currentInvitation.id}/sessions`, {
                time: newTimeMs
            });
            message.success(t('organizer.invitations.success.sessionAdded'));
            setCurrentInvitation(res.data);
            setNewSessionTime(null);
            onSuccess();
        } catch (err: any) {
            message.error(getErrorMessage(err));
        } finally {
            setAddingSession(false);
        }
    };

    const handleDeleteSession = async (sessionId: string) => {
        if (!currentInvitation?.id) return;
        try {
            const res = await axios.delete(`/api/organizer/invitations/${currentInvitation.id}/sessions/${sessionId}`);
            message.success(t('organizer.invitations.success.sessionRemoved'));
            setCurrentInvitation(res.data);
            onSuccess();
        } catch (err: any) {
            message.error(getErrorMessage(err));
        }
    };

    return (
        <Modal
            title={
                <Space>
                    <Title level={4} style={{ margin: 0 }}>
                        {currentInvitation ? t('organizer.invitations.title.manage') : t('organizer.invitations.title.create')}
                    </Title>
                    <Text type="secondary">({adventure.title})</Text>
                    {currentInvitation && (
                        currentInvitation.published ? (
                            <Tag color="green" icon={<CheckCircleOutlined />}>{t('organizer.invitations.published')}</Tag>
                        ) : (
                            <Tag color="gold" icon={<ExclamationCircleOutlined />}>{t('organizer.invitations.unpublished')}</Tag>
                        )
                    )}
                </Space>
            }
            open={open}
            onCancel={onClose}
            footer={null}
            width={720}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSaveInvitation}
                style={{ marginTop: '16px' }}
            >
                {/* SECTION 1: GM & PLAYSPACE */}
                <Card
                    type="inner"
                    title={
                        <Space>
                            <UserOutlined style={{ color: '#1890ff' }} />
                            <Text strong>{t('organizer.invitations.section1.title')}</Text>
                        </Space>
                    }
                    style={{ marginBottom: '20px', borderRadius: '8px' }}
                >
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="gmId"
                                label={t('organizer.invitations.section1.gm')}
                                rules={[{ required: true, message: t('organizer.invitations.section1.gm.required') }]}
                            >
                                <Select placeholder={t('organizer.invitations.section1.gm.placeholder')}>
                                    {gameMasters.map(gm => (
                                        <Select.Option key={gm.id} value={gm.id}>
                                            <Space>
                                                <UserOutlined />
                                                <span>{gm.name} {gm.nickname ? `(${gm.nickname})` : ''}</span>
                                            </Space>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="playSpaceId"
                                label={t('organizer.invitations.section1.playspace')}
                                rules={[{ required: true, message: t('organizer.invitations.section1.playspace.required') }]}
                            >
                                <Select placeholder={t('organizer.invitations.section1.playspace.placeholder')} loading={loadingPlaySpaces}>
                                    {playSpaces.map(ps => (
                                        <Select.Option key={ps.id} value={ps.id}>
                                            <Space>
                                                <EnvironmentOutlined />
                                                <span>
                                                    {ps.online
                                                        ? `Online (${ps.onlinePlatform})`
                                                        : ps.addressGmapsFormatted || 'Physical Space'}
                                                </span>
                                            </Space>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* SECTION 2: PLAYERS, DEADLINE & POLICY */}
                <Card
                    type="inner"
                    title={
                        <Space>
                            <SafetyCertificateOutlined style={{ color: '#722ed1' }} />
                            <Text strong>{t('organizer.invitations.section2.title')}</Text>
                        </Space>
                    }
                    style={{ marginBottom: '20px', borderRadius: '8px' }}
                >
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="approvalRequired"
                                label={t('organizer.invitations.section2.approval')}
                                valuePropName="checked"
                            >
                                <Switch checkedChildren={t('common.yes')} unCheckedChildren={t('common.no')} />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="preexistingPlayers"
                                label={t('organizer.invitations.section2.preexisting')}
                                rules={[{ required: true, message: t('common.failure') }]}
                            >
                                <InputNumber min={0} max={10} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="minTotalPlayers"
                                label={t('organizer.invitations.section2.minPlayers')}
                                rules={[{ required: true, message: t('common.failure') }]}
                            >
                                <InputNumber min={1} max={10} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="maxTotalPlayers"
                                label={t('organizer.invitations.section2.maxPlayers')}
                                rules={[
                                    { required: true, message: t('common.failure') },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (value !== undefined && value > 10) {
                                                return Promise.reject(new Error(t('errors.INVALID_PLAYER_LIMITS')));
                                            }
                                            if (value !== undefined && value < getFieldValue('minTotalPlayers')) {
                                                return Promise.reject(new Error(t('errors.INVALID_PLAYER_LIMITS')));
                                            }
                                            return Promise.resolve();
                                        }
                                    })
                                ]}
                            >
                                <InputNumber min={1} max={10} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="invitationDeadline"
                                label={t('organizer.invitations.section2.deadline')}
                                rules={[{ required: true, message: t('organizer.invitations.section2.deadline.required') }]}
                            >
                                <DatePicker
                                    showTime
                                    format="YYYY-MM-DD HH:mm"
                                    disabledDate={(current) => current && current.isBefore(dayjs().startOf('day'))}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="costPerSession"
                                label={t('organizer.invitations.section2.cost')}
                                rules={[{ required: true, message: t('common.failure') }]}
                            >
                                <InputNumber
                                    min={0}
                                    precision={2}
                                    prefix="€"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="cancelationPolicy"
                        label={t('organizer.invitations.section2.cancellation')}
                        rules={[{ required: true, message: t('organizer.invitations.section2.cancellation.required') }]}
                    >
                        <Input.TextArea
                            rows={3}
                            placeholder={t('organizer.invitations.section2.cancellation.placeholder')}
                        />
                    </Form.Item>
                </Card>

                {/* SAVE INVITATION DETAILS BUTTON */}
                <div style={{ textAlign: 'right', marginBottom: '24px' }}>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={saving}
                        icon={<CheckCircleOutlined />}
                        size="large"
                    >
                        {currentInvitation ? t('organizer.invitations.update') : t('organizer.invitations.save')}
                    </Button>
                </div>
            </Form>

            <Divider />

            {/* SESSIONS MANAGEMENT SECTION */}
            <Card
                type="inner"
                title={
                    <Space>
                        <CalendarOutlined style={{ color: '#52c41a' }} />
                        <Text strong>{t('organizer.invitations.sessions.title')}</Text>
                        {currentInvitation && (
                            <Tag color="blue">{currentInvitation.sessions?.length || 0} / 4</Tag>
                        )}
                    </Space>
                }
                style={{ marginBottom: '20px', borderRadius: '8px' }}
            >
                {!currentInvitation ? (
                    <Alert
                        type="info"
                        showIcon
                        message={t('organizer.invitations.sessions.saveFirst')}
                    />
                ) : (
                    <div>
                        <Paragraph type="secondary" style={{ marginBottom: '16px' }}>
                            {t('organizer.invitations.sessions.desc')}
                            {minSessionDate ? ` (${minSessionDate.format('YYYY-MM-DD HH:mm')} ${t('common.orLater') || 'or later'})` : ''}.
                        </Paragraph>

                        {/* List existing sessions */}
                        {currentInvitation.sessions && currentInvitation.sessions.length > 0 ? (
                            <div style={{ marginBottom: '16px' }}>
                                {currentInvitation.sessions.map((sess, idx) => (
                                    <div
                                        key={sess.id || idx}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '10px 16px',
                                            background: '#f9f9f9',
                                            borderRadius: '6px',
                                            marginBottom: '8px',
                                            border: '1px solid #e8e8e8'
                                        }}
                                    >
                                        <Space>
                                            <Tag color="green">Session {idx + 1}</Tag>
                                            <CalendarOutlined />
                                            <Text strong>{dayjs(sess.time).format('YYYY-MM-DD HH:mm')}</Text>
                                        </Space>

                                        <Popconfirm
                                            title={t('organizer.invitations.removeSessionConfirm')}
                                            onConfirm={() => handleDeleteSession(sess.id!)}
                                            okText={t('common.yes')}
                                            cancelText={t('common.no')}
                                        >
                                            <Button type="text" danger icon={<DeleteOutlined />} size="small">
                                                {t('common.delete')}
                                            </Button>
                                        </Popconfirm>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <Alert
                                type="warning"
                                showIcon
                                message={t('organizer.invitations.sessions.none')}
                                style={{ marginBottom: '16px' }}
                            />
                        )}

                        {/* Add session input */}
                        {(currentInvitation.sessions?.length || 0) < 4 ? (
                            <Row gutter={12} align="middle">
                                <Col flex="auto">
                                    <DatePicker
                                        showTime
                                        format="YYYY-MM-DD HH:mm"
                                        placeholder={t('organizer.invitations.sessions.placeholder')}
                                        value={newSessionTime}
                                        onChange={(val) => setNewSessionTime(val)}
                                        disabledDate={(current) =>
                                            minSessionDate ? current && current.isBefore(minSessionDate.startOf('day')) : false
                                        }
                                        style={{ width: '100%' }}
                                    />
                                </Col>
                                <Col>
                                    <Button
                                        type="dashed"
                                        icon={<PlusOutlined />}
                                        onClick={handleAddSession}
                                        loading={addingSession}
                                        disabled={!newSessionTime}
                                    >
                                        {t('organizer.invitations.sessions.add')}
                                    </Button>
                                </Col>
                            </Row>
                        ) : (
                            <Text type="secondary" italic>{t('organizer.invitations.sessions.maxReached')}</Text>
                        )}
                    </div>
                )}
            </Card>

            {/* MODAL FOOTER ACTIONS (Mobile-friendly stacked vertical buttons) */}
            {currentInvitation && (
                <Space direction="vertical" style={{ width: '100%', marginTop: '24px' }} size="middle">
                    {!currentInvitation.published && (
                        <Button
                            type="primary"
                            block
                            size="large"
                            style={{ background: '#52c41a', borderColor: '#52c41a', height: '44px', fontWeight: 'bold' }}
                            icon={<SendOutlined />}
                            onClick={handlePublishInvitation}
                        >
                            {t('organizer.invitations.publish')}
                        </Button>
                    )}

                    <Popconfirm
                        title={t('organizer.invitations.deleteConfirm')}
                        description={t('organizer.invitations.deleteConfirmDesc')}
                        onConfirm={handleDeleteInvitation}
                        okText={t('common.yes')}
                        cancelText={t('common.no')}
                        disabled={!!currentInvitation.published}
                    >
                        <Button
                            danger
                            block
                            size="middle"
                            icon={<DeleteOutlined />}
                            disabled={!!currentInvitation.published}
                            title={currentInvitation.published ? t('organizer.invitations.cannotDeleteTooltip') : t('organizer.invitations.delete')}
                        >
                            {currentInvitation.published ? t('organizer.invitations.cannotDelete') : t('organizer.invitations.delete')}
                        </Button>
                    </Popconfirm>

                    <Button block size="middle" onClick={onClose}>
                        {t('common.cancel')}
                    </Button>
                </Space>
            )}
        </Modal>
    );
};
